import {
  authenticatedFetch,
  extractApiErrorMessage,
  resolvePeriverseApiBaseUrl,
} from '../shared';
import {
  FacilityDtoSchema,
  FacilitiesFilterParams,
  CreateFacilityDto,
  PaginatedFacilitiesResponseDto,
  PaginatedFacilitiesResponseDtoSchema,
  UpdateFacilityDto,
  type FacilityDto,
} from '@peritasai/types';

type BaseUrlResolver = () => string;

export class FacilitiesApiClient {
  constructor(
    private readonly resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
  ) {}

  async getFacilities(
    options: FacilitiesFilterParams = {}
  ): Promise<PaginatedFacilitiesResponseDto> {
    const queryParams = new URLSearchParams();

    if (options.search) {
      queryParams.append('search', options.search);
    }

    const page = options.page !== undefined ? options.page + 1 : 1;
    const limit = options.limit ?? 10;
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    const url = `${this.resolveBaseUrl()}/facilities?${queryParams.toString()}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return PaginatedFacilitiesResponseDtoSchema.parse(await response.json());
  }

  async createFacility(dto: CreateFacilityDto): Promise<FacilityDto> {
    const url = `${this.resolveBaseUrl()}/facilities`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return FacilityDtoSchema.parse(await response.json());
  }

  async updateFacility(id: string, dto: UpdateFacilityDto): Promise<FacilityDto> {
    const url = `${this.resolveBaseUrl()}/facilities/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return FacilityDtoSchema.parse(await response.json());
  }
}

export const createFacilitiesApiClient = (
  resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
): FacilitiesApiClient => new FacilitiesApiClient(resolveBaseUrl);

export const facilitiesApiClient = new FacilitiesApiClient();

export type { BaseUrlResolver };
